import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const pillars = [
  {
    number: '01',
    title: 'Adaptogenic Power',
    description:
      'Compounds calibrated to the HPA axis. Ashwagandha, Rhodiola, Holy Basil — each works bidirectionally, helping your body regulate exactly what it needs.',
    icon: '/science-icons/adaptogenic-power.png',
    stat: '2,000+',
    statLabel: 'years of documented use',
  },
  {
    number: '02',
    title: 'Mushroom Intelligence',
    description:
      'Functional fungi at the intersection of neurology and immunology. Lion\'s Mane, Reishi, Cordyceps — cultivated for maximum beta-glucan density.',
    icon: '/science-icons/mushroom-intelligence.png',
    stat: '3,000+',
    statLabel: 'peer-reviewed studies',
  },
  {
    number: '03',
    title: 'Bioavailable Formulas',
    description:
      'Full-spectrum extraction: hot-water for polysaccharides, ethanol for triterpenes. Every compound delivered in the precise form your cells can use.',
    icon: '/science-icons/bioavailable-formulas.png',
    stat: '94%',
    statLabel: 'average absorption rate',
  },
];

function SpecimenCard({ pillar, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 48 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.18, ease: [0.22, 1, 0.36, 1] }}
      className="group flex flex-col items-center text-center px-6 md:px-10 relative"
    >
      {/* Vertical separator — only between items */}
      {index > 0 && (
        <div className="hidden md:block absolute left-0 top-0 bottom-0 w-px bg-berleto-sage/15" />
      )}

      {/* Number tag */}
      <p
        className="text-xs font-medium tracking-[0.2em] uppercase mb-7"
        style={{ color: '#B8704D' }}
      >
        {pillar.number}
      </p>

      {/* Icon */}
      <div className="relative mb-8">
        <motion.img
          src={pillar.icon}
          alt={pillar.title}
          className="w-24 h-24 md:w-28 md:h-28 object-contain mx-auto"
          style={{ filter: 'drop-shadow(0 2px 12px rgba(184,112,77,0.3))' }}
          whileHover={{ scale: 1.06 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>

      {/* Stat */}
      <div className="mb-5">
        <p className="text-3xl font-light" style={{ color: '#E8DCC4' }}>
          {pillar.stat}
        </p>
        <p className="text-xs tracking-widest uppercase mt-1" style={{ color: '#8B9A8B' }}>
          {pillar.statLabel}
        </p>
      </div>

      {/* Thin rule */}
      <div className="w-8 h-px mb-5" style={{ background: '#B8704D' }} />

      {/* Title */}
      <h3 className="text-lg font-medium tracking-wide mb-3" style={{ color: '#FAF9F6' }}>
        {pillar.title}
      </h3>

      {/* Description */}
      <p
        className="text-sm leading-relaxed max-w-xs"
        style={{ color: 'oklch(65% 0.02 150)' }}
      >
        {pillar.description}
      </p>
    </motion.div>
  );
}

export function ScienceSection() {
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, margin: '-60px' });

  return (
    <section
      className="relative py-28 overflow-hidden"
      style={{ background: '#1A2A1A' }}
      data-bubble-theme="light"
    >
      {/* Subtle radial atmosphere */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(184,112,77,0.07) 0%, transparent 60%)',
        }}
      />

      {/* Fine dot texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage:
            'radial-gradient(circle, rgba(232,220,196,0.12) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />

      <div className="container mx-auto px-6 relative">

        {/* Header */}
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 32 }}
          animate={headerInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-20"
        >
          {/* Eyebrow */}
          <p
            className="text-xs font-medium tracking-[0.25em] uppercase mb-6 text-center"
            style={{ color: '#B8704D' }}
          >
            The Research Behind Nature
          </p>

          {/* Main headline */}
          <h2
            className="text-4xl md:text-6xl font-light text-center leading-tight max-w-3xl mx-auto"
            style={{ color: '#FAF9F6' }}
          >
            Ancient intelligence.
            <br />
            <span style={{ color: '#8B9A8B' }}>Modern precision.</span>
          </h2>

          {/* Subtitle */}
          <p
            className="mt-6 text-base text-center max-w-xl mx-auto leading-relaxed"
            style={{ color: 'oklch(62% 0.025 150)' }}
          >
            Every Berleto formula is built on decades of clinical research and thousands of years of
            botanical tradition — validated, extracted, and delivered with exacting standards.
          </p>
        </motion.div>

        {/* Horizontal rule */}
        <div className="w-full h-px mb-20" style={{ background: 'rgba(139,154,139,0.15)' }} />

        {/* Pillars grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-0">
          {pillars.map((pillar, i) => (
            <SpecimenCard key={pillar.number} pillar={pillar} index={i} />
          ))}
        </div>

        {/* Bottom rule + footnote */}
        <div className="mt-24">
          <div className="w-full h-px mb-8" style={{ background: 'rgba(139,154,139,0.15)' }} />
          <p className="text-xs text-center tracking-widest uppercase" style={{ color: '#8B9A8B' }}>
            All formulations third-party tested &nbsp;·&nbsp; Standardized extracts &nbsp;·&nbsp; No fillers
          </p>
        </div>
      </div>
    </section>
  );
}
